import React, { useEffect, useState } from 'react'
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap'
import McDataServices from '../../services/McDataServices'
import { useTranslation } from 'react-i18next'
import { RSelect } from '../../components/Component'
import { toast } from 'react-toastify'

const PackageAddModal = ({ isOpen, toggle, onRefresh, copyPackageId }) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [hsOptions, setHsOptions] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [hsInput, setHsInput] = useState('')

  const [b2bPackages, setB2bPackages] = useState([])

  const defaultRuleMap = {
    HS_CODE_COUNT: 0,
    MARKET_REPORT_TOTAL_SEARCH_COUNT: 0,
    MARKET_REPORT_EXCEL_DOWNLOAD_COUNT: 0,
    MARKET_REPORT_DAILY_SEARCH_COUNT: 0,
    HS_CODE_SET: '',
    HS_CODE_AI_SEARCH_COUNT: 0,
    PRODUCT_DETAIL_SEARCH_COUNT: 0,
    SEARCH_COUNTRY_SET: '',
    P7_FIRM_DOWNLOAD_COUNT: 0,
    TRADE_REGISTRY_FIRM_VIEW_COUNT: 0,
    ALL_FIRM_DOWNLOAD_COUNT: 0,
    P7_FIRM_VIEW_COUNT: 0,
    ALL_FIRM_VIEW_COUNT: 0,
    TRADE_REGISTRY_FIRM_DOWNLOAD_COUNT: 0,
    CAMPAIGN_FIRM_DOWNLOAD_COUNT: 0
  }

  const defaultFormData = {
    name: '',
    price: 0,
    currency: 'TRY',
    durationYear: 1,
    durationMonth: 0,
    durationDay: 0,
    b2bPackageId: '',
    ruleMap: { ...defaultRuleMap }
  }

  const [formData, setFormData] = useState(defaultFormData)

  useEffect(() => {
    if (isOpen) {
      fetchOptions()
      if (copyPackageId) {
        fetchCopyPackage(copyPackageId)
      } else {
        setFormData(defaultFormData)
      }
    } else {
      setFormData(defaultFormData)
    }
  }, [isOpen, copyPackageId])

  const fetchCopyPackage = async (id) => {
    try {
      const data = await McDataServices.getPackageById(id)
      if (data) {
        const newRuleMap = { ...defaultRuleMap }
        if (data.rules) {
          data.rules.forEach(r => {
            if (newRuleMap[r.type] !== undefined) {
              newRuleMap[r.type] = r.parameters?.count ?? r.parameters?.allowedHsCodes ?? r.parameters?.allowedSearchCountries ?? newRuleMap[r.type]
            }
          })
        }
        setFormData({
          name: '',
          price: data.price,
          currency: data.currency,
          durationYear: data.durationYear,
          durationMonth: data.durationMonth,
          durationDay: data.durationDay,
          b2bPackageId: data.b2bPackageId || '',
          ruleMap: newRuleMap
        })
      }
    } catch (error) {
      console.error('Failed to fetch package for copy', error)
      toast.error(t('admin.modal.fetchError', 'Failed to fetch package details.'))
    }
  }

  const fetchOptions = async () => {
    try {
      const hsResult = await McDataServices.getHsCodes()
      setHsOptions(hsResult || [])

      const countryResult = await McDataServices.getCountries()
      if (countryResult?.search_countries) {
        const sortedCountries = countryResult.search_countries.map(c => ({
          value: c.countryCode,
          label: c.label
        })).sort((a, b) => a.label.localeCompare(b.label))
        setCountryOptions(sortedCountries)
      }

      const b2bResult = await McDataServices.getB2bPackages()
      if (b2bResult) {
        const options = b2bResult.map(p => ({
          value: p.id,
          label: p.name
        }))
        setB2bPackages(options)
      }
    } catch (error) {
      console.error('Failed to fetch options', error)
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const rules = Object.entries(formData.ruleMap).map(([type, val]) => {
        const parameters = {}
        if (type.includes('_SET')) {
          const paramName = type === 'HS_CODE_SET' || type === 'B2B_HS_CODE_SET' ? 'allowedHsCodes' : 'allowedSearchCountries'
          parameters[paramName] = val
        } else {
          parameters.count = val
        }
        return { type, parameters }
      })

      const payload = {
        name: formData.name,
        price: formData.price,
        currency: formData.currency,
        durationYear: formData.durationYear,
        durationMonth: formData.durationMonth,
        durationDay: formData.durationDay,
        b2bPackageId: formData.b2bPackageId,
        rules
      }

      await McDataServices.createPackage(payload)
      toast.success(t('admin.modal.createSuccess', 'Package created successfully.'))
      onRefresh()
      toggle()
    } catch (error) {
      const errorData = error?.response?.data || error
      if (errorData?.type === 'UniqueException' && errorData?.detail?.fieldName === 'name') {
        toast.error(t('admin.modal.uniqueNameError', 'Bu paket ismi zaten kullanılıyor. Lütfen farklı bir isim giriniz.'))
      } else {
        const errorDetail = typeof errorData?.detail === 'string' ? errorData.detail : undefined
        toast.error(errorDetail || t('admin.modal.createError', 'Paket oluşturulurken bir hata oluştu.'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleRuleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      ruleMap: {
        ...prev.ruleMap,
        [key]: value
      }
    }))
  }

  const renderRuleInputs = () => {
    const ruleEntries = Object.entries(formData.ruleMap)

    const setRules = ruleEntries.filter(([key]) => key.includes('_SET'))
    const otherRules = ruleEntries.filter(([key]) => !key.includes('_SET'))

    const renderItem = ([key, val], isSet) => {
      const translatedKey = t(`payment.rules.${key}`, key)
      const isCountrySet = key.includes('SEARCH_COUNTRY_SET')
      const isHsSet = key.includes('HS_CODE_SET')

      if (isSet) {
        const currentInput = isHsSet ? hsInput : ''
        const showOptions = !isHsSet || currentInput.length >= 3

        const options = isCountrySet ? countryOptions : (showOptions ? hsOptions : [])
        const currentValues = val
          ? val.toString().split(',').map(v => {
            const found = options.find(o => o.value === v.trim()) || hsOptions.find(o => o.value === v.trim())
            return found || { value: v.trim(), label: v.trim() }
          }).filter(Boolean)
          : []

        return (
          <Col xl="4" lg="6" md="12" key={key} className="mb-2">
            <FormGroup className="mb-0">
              <Label className="modern-label">{translatedKey}</Label>
              <RSelect
                isMulti
                options={options}
                value={currentValues}
                onInputChange={(val) => {
                  if (isHsSet) setHsInput(val)
                }}
                noOptionsMessage={() => !showOptions ? t('min_3_char', 'Please enter at least 3 characters') : t('no_option', 'No options')}
                menuPortalTarget={document.body}
                styles={{
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: '34px', height: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                  input: (base) => ({ ...base, margin: '0', padding: '0' }),
                  indicatorsContainer: (base) => ({ ...base, height: '34px' })
                }}
                onChange={(selected) => {
                  const joinValue = selected ? selected.map(s => s.value).join(',') : ''
                  handleRuleChange(key, joinValue)
                }}
                className="react-select-container-sm"
              />
            </FormGroup>
          </Col>
        )
      }

      return (
        <Col xl="2" lg="3" md="4" sm="6" key={key} className="mb-2">
          <FormGroup className="mb-0">
            <Label className="modern-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                   title={translatedKey}>
              {translatedKey}
            </Label>
            <Input
              type="number"
              value={val}
              onChange={(e) => handleRuleChange(key, Number(e.target.value))}
              className="modern-input w-100"
            />
          </FormGroup>
        </Col>
      )
    }

    return (
      <>
        <Row className="mb-3 gx-3">
          {setRules.map(item => renderItem(item, true))}
        </Row>
        <Row className="gx-3 gy-3">
          {otherRules.map(item => renderItem(item, false))}
        </Row>
      </>
    )
  }

  return (
    <>
      <style>
        {`
          .super-wide-modal .modal-content {
            background-color: #f3f4f6 !important;
            border: none !important;
            border-radius: 8px !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
          }
          .super-wide-modal {
            max-width: 95vw !important;
            width: 95vw !important;
          }
          .modern-card {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          .modern-card-header {
            background-color: transparent !important;
            border-bottom: none !important;
            padding: 0.5rem 0.25rem 1rem 0.25rem !important;
          }
          .modern-card-body {
             padding: 0.25rem !important;
          }
          .modern-input {
            border: 1px solid #e5e7eb !important;
            border-radius: 4px !important;
            padding: 0.375rem 0.75rem !important;
            font-size: 0.8125rem !important;
            background-color: #ffffff !important;
            color: #374151 !important;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
            height: 34px !important;
          }
          .modern-input:focus {
            border-color: #8b5cf6 !important;
            box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2) !important;
            outline: none !important;
          }
          .modern-input-error {
            border-color: #ef4444 !important;
          }
          .modern-input-error:focus {
            border-color: #ef4444 !important;
            box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2) !important;
            outline: none !important;
          }
          .modern-label {
            font-size: 0.7rem !important;
            font-weight: 600 !important;
            color: #6b7280 !important;
            margin-bottom: 0.4rem !important;
          }
          .modal-header-modern {
             background-color: #e5e7eb !important;
             border-bottom: none !important;
             padding: 0.75rem 1.25rem !important;
             border-radius: 8px 8px 0 0 !important;
          }
          .modal-footer-modern {
             background-color: #e5e7eb !important;
             border-top: none !important;
             padding: 0.75rem 1.25rem !important;
             border-radius: 0 0 8px 8px !important;
          }
          .btn-modern-primary {
             background-color: #6366f1 !important;
             border-color: #6366f1 !important;
             border-radius: 4px !important;
             font-weight: 500 !important;
             padding: 0.375rem 1.5rem !important;
          }
          .btn-modern-secondary {
             background-color: #4b5563 !important;
             border-color: #4b5563 !important;
             color: #ffffff !important;
             border-radius: 4px !important;
             font-weight: 500 !important;
             padding: 0.375rem 1.5rem !important;
          }
        `}
      </style>
      <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered super-wide-modal" size="xl">
        <ModalHeader toggle={toggle} className="modal-header-modern">
          <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem', color: '#374151' }}>{t('admin.modal.addNewPackage', 'Add New Package')}</h6>
        </ModalHeader>
        <Form onSubmit={handleSave}>
          <ModalBody className="p-4">
            <Card className="mb-4 modern-card">
              <CardBody className="modern-card-body position-relative">
                <div style={{ position: 'absolute', left: '-0.5rem', top: 0, bottom: 0, width: '4px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
                <Row className="gx-3">
                  <Col md="2">
                    <FormGroup className="mb-1">
                      <Label className="modern-label">{t('admin.modal.packageName', 'Package Name')}</Label>
                      <Input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className={`modern-input ${!formData.name?.trim() ? 'modern-input-error' : ''}`}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-1">
                      <Label className="modern-label">{t('admin.modal.price', 'Price')}</Label>
                      <Input
                        type="number"
                        required
                        value={formData.price}
                        onChange={(e) => handleChange('price', Number(e.target.value))}
                        className="modern-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-1">
                      <Label className="modern-label">{t('admin.modal.currency', 'Currency')}</Label>
                      <Input
                        type="select"
                        value={formData.currency}
                        onChange={(e) => handleChange('currency', e.target.value)}
                        className="modern-input"
                      >
                        <option value="TRY">TRY</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-1">
                      <Label className="modern-label">{t('admin.modal.year', 'Year')}</Label>
                      <Input type="number" value={formData.durationYear} onChange={(e) => handleChange('durationYear', Number(e.target.value))} className="modern-input"/>
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-1">
                      <Label className="modern-label">{t('admin.modal.month', 'Month')}</Label>
                      <Input type="number" value={formData.durationMonth} onChange={(e) => handleChange('durationMonth', Number(e.target.value))} className="modern-input"/>
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-1">
                      <Label className="modern-label">{t('admin.modal.day', 'Day')}</Label>
                      <Input type="number" value={formData.durationDay} onChange={(e) => handleChange('durationDay', Number(e.target.value))} className="modern-input"/>
                    </FormGroup>
                  </Col>
                  <Col md="5">
                    <FormGroup className="mb-1">
                      <Label className="modern-label">{t('admin.modal.mailingPackage', 'Mailing Paketi')}</Label>
                      <RSelect
                        options={b2bPackages}
                        value={b2bPackages.find(p => p.value === formData.b2bPackageId)}
                        onChange={(selected) => handleChange('b2bPackageId', selected?.value || '')}
                        placeholder={t('admin.modal.selectB2bPackage', 'Seçiniz...')}
                        isClearable
                        className="react-select-container-sm"
                        styles={{
                          control: (base) => ({ ...base, minHeight: '34px', height: '34px', border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }),
                          indicatorsContainer: (base) => ({ ...base, height: '34px' }),
                          valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                          input: (base) => ({ ...base, margin: '0', padding: '0' })
                        }}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-0 modern-card">
              <CardHeader className="modern-card-header">
                <h6 className="mb-0 fw-bold" style={{ fontSize: '0.85rem', color: '#4b5563' }}>{t('admin.modal.packageRules', 'Paket Kuralları')}</h6>
              </CardHeader>
              <CardBody className="modern-card-body">
                {renderRuleInputs()}
              </CardBody>
            </Card>
          </ModalBody>
          <ModalFooter className="modal-footer-modern">
            <Button className="btn-modern-secondary" onClick={toggle} disabled={saving}>
              {t('admin.modal.cancel', 'İptal')}
            </Button>
            <Button className="btn-modern-primary" type="submit" disabled={saving || !formData.name?.trim()}>
              {saving ? <Spinner size="sm"/> : t('admin.modal.save', 'Kaydet')}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  )
}

export default PackageAddModal
